import { Component, OnInit } from '@angular/core';
import { MiaV3Service } from '../services/mia-v3.service';
import { AutorizacaoService } from '../services/autorizacao.service';
import { custoEmReaisFormatado } from './custo-ia';

/**
 * Rótulo e recado de cada motivo de não-venda.
 *
 * O dono da loja não lê enum: ele lê "montou o carrinho e sumiu". A dica existe
 * porque número sem próximo passo não muda nada na operação.
 */
const MOTIVOS: { [chave: string]: { titulo: string, dica: string, destaque?: boolean } } = {
  ABANDONO_CARRINHO: {
    titulo: 'Montou o carrinho e sumiu',
    dica: 'Perda mais recuperável: essas pessoas já tinham escolhido o que queriam.',
    destaque: true
  },
  PAGAMENTO_FALHOU: {
    titulo: 'O pagamento não passou',
    dica: 'Se este número for alto, o problema está no meio de pagamento, não na conversa.'
  },
  FORA_AREA: {
    titulo: 'Endereço fora da área de entrega',
    dica: 'Perda real, mas fora do alcance da Mia — é decisão de área de cobertura.'
  },
  LOJA_FECHADA: {
    titulo: 'A loja estava fechada',
    dica: 'Vale olhar se a procura está concentrada em algum horário que ainda não abre.'
  },
  ITEM_INDISPONIVEL: {
    titulo: 'Procurou algo que você não tem',
    dica: 'Cada um destes é um pedido de cardápio que o cliente fez sem querer.'
  },
  DESISTENCIA: {
    titulo: 'Desistiu durante a conversa',
    dica: 'Desistência explícita costuma ser objeção de preço ou de tempo de entrega.'
  }
};

interface MotivoNaTela {
  chave: string;
  titulo: string;
  dica: string;
  destaque: boolean;
  quantidade: number;
  percentual: number;
}

@Component({
  selector: 'app-painel-mia-v3',
  templateUrl: './painel-mia-v3.component.html',
  styleUrls: ['./painel-mia-v3.component.scss']
})
export class PainelMiaV3Component implements OnInit {
  periodos = [
    { dias: 1, rotulo: 'Hoje' },
    { dias: 7, rotulo: '7 dias' },
    { dias: 30, rotulo: '30 dias' }
  ];

  diasSelecionados = 7;
  personalizado = false;
  dataDe = '';
  dataAte = '';
  carregando = true;
  erro: string | null = null;

  resumo: any = null;
  motivos: MotivoNaTela[] = [];

  private usuario: any = {};

  constructor(private miaV3Service: MiaV3Service,
              private autorizacaoService: AutorizacaoService) {}

  ngOnInit(): void {
    this.usuario = this.autorizacaoService.getUser() || {};
    this.carregue();
  }

  /** Exportação de transcrição é sensível: só admin do MeuCardápio vê o botão. */
  get ehAdmin(): boolean {
    return !!(this.usuario.admin || this.usuario.superadmin);
  }

  selecionePeriodo(dias: number): void {
    if (dias === this.diasSelecionados && !this.personalizado) return;

    this.personalizado = false;
    this.diasSelecionados = dias;
    this.carregue();
  }

  /** Abre os campos de/até já preenchidos com o período do chip atual. */
  ativePeriodoPersonalizado(): void {
    if (this.personalizado) return;

    this.personalizado = true;
    const hoje = new Date();
    const inicio = new Date();
    inicio.setDate(inicio.getDate() - (this.diasSelecionados - 1));
    this.dataDe = this.formateDia(inicio);
    this.dataAte = this.formateDia(hoje);
  }

  get periodoValido(): boolean {
    return !!this.dataDe && !!this.dataAte && this.dataDe <= this.dataAte;
  }

  apliquePeriodo(): void {
    if (!this.periodoValido) return;
    this.carregue();
  }

  carregue(): void {
    this.carregando = true;
    this.erro = null;

    this.miaV3Service.obtenhaMetricas(this.filtroAtual())
      .then(resposta => {
        this.resumo = resposta.resumo;
        this.motivos = this.monteMotivos(resposta.motivos);
      })
      .catch(() => this.erro = 'Não consegui carregar os números da Mia agora.')
      .then(() => this.carregando = false);
  }

  /** Baixa o CSV do mesmo período que está na tela (sessão autentica o GET). */
  exporteConversas(): void {
    window.location.href = this.miaV3Service.urlExportacaoDeConversas(this.filtroAtual());
  }

  private filtroAtual(): { dias?: number, de?: string, ate?: string } {
    if (this.personalizado && this.periodoValido) return { de: this.dataDe, ate: this.dataAte };
    return { dias: this.diasSelecionados };
  }

  private formateDia(data: Date): string {
    const doisDigitos = (valor: number) => String(valor).padStart(2, '0');
    return `${data.getFullYear()}-${doisDigitos(data.getMonth() + 1)}-${doisDigitos(data.getDate())}`;
  }

  private monteMotivos(motivos: any[]): MotivoNaTela[] {
    const maior = Math.max(1, ...motivos.map(motivo => Number(motivo.quantidade) || 0));

    return motivos
      .filter(motivo => MOTIVOS[motivo.motivo])
      .map(motivo => ({
        chave: motivo.motivo,
        titulo: MOTIVOS[motivo.motivo].titulo,
        dica: MOTIVOS[motivo.motivo].dica,
        destaque: !!MOTIVOS[motivo.motivo].destaque,
        quantidade: Number(motivo.quantidade) || 0,
        percentual: Math.round((Number(motivo.quantidade) || 0) / maior * 100)
      }));
  }

  get temDados(): boolean {
    return !!this.resumo && Number(this.resumo.atendimentos) > 0;
  }

  /** Denominador: só atendimentos em que o cliente queria comprar. */
  get elegiveis(): number {
    return Number(this.resumo?.elegiveis) || 0;
  }

  get vendas(): number {
    return Number(this.resumo?.vendas) || 0;
  }

  get conversao(): number {
    return this.elegiveis ? Math.round(this.vendas / this.elegiveis * 100) : 0;
  }

  get faturamento(): number {
    return Number(this.resumo?.faturamento) || 0;
  }

  get ticketMedio(): number {
    return this.vendas ? this.faturamento / this.vendas : 0;
  }

  get pedidosCancelados(): number {
    return Number(this.resumo?.pedidosCancelados) || 0;
  }

  get valorCancelado(): number {
    return Number(this.resumo?.valorCancelado) || 0;
  }

  get falhasTecnicas(): number {
    return Number(this.resumo?.falhasTecnicas) || 0;
  }

  get inconclusivos(): number {
    return Number(this.resumo?.inconclusivos) || 0;
  }

  get atendimentos(): number {
    return Number(this.resumo?.atendimentos) || 0;
  }

  get percentualInconclusivo(): number {
    return this.atendimentos ? Math.round(this.inconclusivos / this.atendimentos * 100) : 0;
  }

  get escalados(): number {
    return Number(this.resumo?.escalados) || 0;
  }

  get emAndamento(): number {
    return Number(this.resumo?.emAndamento) || 0;
  }

  /** Conversão de quem passou por atendente humano, para comparar com a da Mia. */
  get conversaoEscalados(): number | null {
    const escalados = this.escalados;
    if (!escalados) return null;

    return Math.round((Number(this.resumo?.escaladosComVenda) || 0) / escalados * 100);
  }

  get perdas(): number {
    return Math.max(0, this.elegiveis - this.vendas);
  }

  get custoUsd(): number {
    return Number(this.resumo?.custoUsd) || 0;
  }

  get custoFormatado(): string {
    return custoEmReaisFormatado(this.custoUsd);
  }

  get custoPorAtendimento(): string | null {
    return this.atendimentos ? custoEmReaisFormatado(this.custoUsd / this.atendimentos) : null;
  }
}
